/**
 * AcademyContext — 멀티테넌트 "현재 학원" 상태
 *
 * - 로그인 사용자가 속한 학원 목록을 로드 (운영 / 가입 구분)
 * - 지금 보고 있는 학원(currentAcademy) 추적 + 전환
 * - 현재 학원에서의 내 권한(myRole) 제공
 *
 * AuthContext 안쪽에 위치해야 한다 (useAuth 의존).
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import academyService from '../services/academyService';

const AcademyContext = createContext();

export const useAcademy = () => useContext(AcademyContext);

const CURRENT_KEY = 'topgrade.currentAcademy';
const MODE_KEY = 'topgrade.mode';

// 학원 입장(운영) 역할
const OPERATOR_ROLES = ['owner', 'teacher', 'assistant'];

export const AcademyProvider = ({ children }) => {
  const { userData } = useAuth();
  const [myAcademies, setMyAcademies] = useState([]);
  const [currentAcademyId, setCurrentAcademyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadedUid, setLoadedUid] = useState(null); // myAcademies가 어느 uid 기준인지
  const [storedMode, setStoredMode] = useState(() => {
    try { return localStorage.getItem(MODE_KEY) === 'academy' ? 'academy' : 'personal'; } catch { return 'personal'; }
  });

  /** 개인/학원 모드 전환 (데모 등 accountType 없는 경우용) */
  const setMode = useCallback((m) => {
    setStoredMode(m);
    try { localStorage.setItem(MODE_KEY, m); } catch {}
  }, []);

  // accountType이 모드를 결정 — 학원 계정은 항상 학원모드, 학부모는 개인모드.
  // accountType이 없는 계정(데모 등)만 storedMode를 따른다.
  const mode = userData?.accountType === 'academy' ? 'academy'
    : userData?.accountType === 'parent' ? 'personal'
    : storedMode;

  /** 내 학원 목록 로드 */
  const loadAcademies = useCallback(async () => {
    if (!userData?.uid) {
      setMyAcademies([]);
      setCurrentAcademyId(null);
      setLoadedUid(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const list = await academyService.getUserAcademies(userData.uid);
      setMyAcademies(list);
      // 마지막으로 보던 학원 복원 (없으면 첫 학원)
      let saved = null;
      try { saved = localStorage.getItem(CURRENT_KEY); } catch {}
      const valid = list.find((a) => a.id === saved);
      setCurrentAcademyId(valid ? saved : (list[0]?.id || null));
      setLoadedUid(userData.uid);
    } catch (e) {
      console.error('[Academy] 학원 목록 로드 실패:', e);
    } finally {
      setLoading(false);
    }
  }, [userData?.uid]);

  useEffect(() => { loadAcademies(); }, [loadAcademies]);

  /** 현재 학원 전환 */
  const selectAcademy = useCallback((aid) => {
    setCurrentAcademyId(aid);
    try { localStorage.setItem(CURRENT_KEY, aid); } catch {}
  }, []);

  const currentAcademy = myAcademies.find((a) => a.id === currentAcademyId) || null;
  const myRole = currentAcademy?.myRole || null;

  // "내 학원" 두 섹션: 운영 중인 학원 / 가입한 학원
  const operatedAcademies = myAcademies.filter((a) => OPERATOR_ROLES.includes(a.myRole));
  const joinedAcademies = myAcademies.filter((a) => a.myRole === 'parent');
  const operatedAcademy = operatedAcademies[0] || null;

  // (모드 자동 복귀 제거 — 데모 진입 풋바 깜빡임 방지. 모드 전환은 사용자가 명시적으로만)

  const value = {
    myAcademies,
    operatedAcademies,   // 원장·강사·조교로 속한 학원
    operatedAcademy,     // 대표 운영 학원 (첫 번째)
    joinedAcademies,     // 학부모로 가입한 학원
    currentAcademy,
    currentAcademyId,
    myRole,
    isOperator: OPERATOR_ROLES.includes(myRole),
    mode,                // 'personal' | 'academy'
    setMode,
    loading,
    loadedUid,           // myAcademies가 로드 완료된 uid (null이면 미로드)
    selectAcademy,
    reloadAcademies: loadAcademies,
  };

  return (
    <AcademyContext.Provider value={value}>
      {children}
    </AcademyContext.Provider>
  );
};
